import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import AdminService from "../../../api/services/adminService";
import {
  ALLOCATION_TYPES,
  CONFIG_STATUS,
  COPY_MODE_HINTS,
  COPY_MODE_LABELS,
  COPY_MODES,
  LEVERAGE_MODES,
  MARGIN_MODES,
  MARKETS,
  ORDER_TYPES,
  PORTFOLIO_VISIBILITY,
  SETTLEMENT_FREQUENCIES,
  buildMasterConfigPayload,
  emptyMasterConfig,
  formatCopyNumber,
  mergeMasterConfig,
  normalizeCopyMode,
  parsePairInput,
  spotPairSymbol,
  validateMasterConfig,
} from "./masterConfigUtils";
import "./MasterTraderConfigModal.css";

const Field = ({ label, hint, children, className = "" }) => (
  <label className={`mtc-field ${className}`.trim()}>
    <span className="mtc-field__label">{label}</span>
    {children}
    {hint ? <span className="mtc-field__hint">{hint}</span> : null}
  </label>
);

const Section = ({ title, icon, children }) => (
  <section className="mtc-section">
    <h4 className="mtc-section__title">
      <i className={`fas ${icon}`} /> {title}
    </h4>
    <div className="mtc-section__grid">{children}</div>
  </section>
);

const MasterTraderConfigModal = ({ master, onClose, onSaved }) => {
  const masterId = master?._id;
  const [form, setForm] = useState(() => emptyMasterConfig(master));
  const [pairDraft, setPairDraft] = useState("");
  const [spotSymbols, setSpotSymbols] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [riskState, setRiskState] = useState(null);
  const [configVersion, setConfigVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [grandfathered, setGrandfathered] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingConfig(true);
      setError("");
      const [configRes, pairsRes, versionsRes, gfRes] = await Promise.all([
        AdminService.getMasterTraderConfiguration(masterId),
        AdminService.getPairsList({ status: "Active" }),
        AdminService.getMasterConfigVersions(masterId, { page: 1, limit: 20 }),
        AdminService.getMasterGrandfatheredFollowers(masterId),
      ]);
      if (cancelled) return;

      if (configRes?.success && configRes.data) {
        setForm(mergeMasterConfig(configRes.data));
        setIsEdit(true);
        setRiskState(configRes.data.riskState || null);
        setConfigVersion(configRes.data.configVersion ?? null);
      } else {
        setForm(emptyMasterConfig(master));
        setIsEdit(false);
        setRiskState(null);
        setConfigVersion(null);
        const msg = String(configRes?.message || "");
        if (msg && !msg.toLowerCase().includes("no configuration")) {
          setError(msg);
        }
      }

      setVersions(Array.isArray(versionsRes?.data) ? versionsRes.data : []);
      setGrandfathered(Array.isArray(gfRes?.data) ? gfRes.data : []);

      const list = Array.isArray(pairsRes?.data) ? pairsRes.data : [];
      const symbols = list
        .map(spotPairSymbol)
        .filter(Boolean)
        .filter((s, i, arr) => arr.indexOf(s) === i)
        .sort();
      setSpotSymbols(symbols);
      setLoadingConfig(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [master, masterId]);

  const setTop = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setNested = (group, key, value) =>
    setForm((f) => ({ ...f, [group]: { ...f[group], [key]: value } }));

  const toggleMarket = (market) => {
    setForm((f) => {
      const has = f.allowedMarkets.includes(market);
      let next = has
        ? f.allowedMarkets.filter((m) => m !== market)
        : [...f.allowedMarkets, market];
      if (!next.length) next = [market];
      return {
        ...f,
        allowedMarkets: next,
        spotSettings: {
          ...f.spotSettings,
          enabled: next.includes("SPOT"),
        },
        futuresSettings: {
          ...f.futuresSettings,
          enabled: next.includes("FUTURES"),
        },
      };
    });
  };

  const togglePair = (symbol) => {
    setForm((f) => {
      const has = f.allowedPairs.includes(symbol);
      return {
        ...f,
        allowedPairs: has
          ? f.allowedPairs.filter((p) => p !== symbol)
          : [...f.allowedPairs, symbol],
      };
    });
  };

  const toggleOrderType = (type) => {
    setForm((f) => {
      const current = f.spotSettings.allowedOrderTypes || [];
      const has = current.includes(type);
      const next = has ? current.filter((t) => t !== type) : [...current, type];
      return {
        ...f,
        spotSettings: {
          ...f.spotSettings,
          allowedOrderTypes: next.length ? next : [type],
        },
      };
    });
  };

  const addDraftPairs = () => {
    const extra = parsePairInput(pairDraft);
    if (!extra.length) return;
    setForm((f) => ({
      ...f,
      allowedPairs: Array.from(new Set([...f.allowedPairs, ...extra])),
    }));
    setPairDraft("");
  };

  const visibleSpotSymbols = useMemo(() => {
    if (!form.allowedMarkets.includes("SPOT")) return [];
    return spotSymbols;
  }, [form.allowedMarkets, spotSymbols]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    const payload = buildMasterConfigPayload(form);
    const validation = validateMasterConfig(payload);
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    try {
      let res = await AdminService.upsertMasterTraderConfiguration(masterId, payload);
      if (
        res?.success === false &&
        String(res?.message || "").toLowerCase().includes("already exists")
      ) {
        res = await AdminService.upsertMasterTraderConfiguration(masterId, payload);
      }
      if (res?.success) {
        const saved = res.data && typeof res.data === "object" ? res.data : payload;
        const gfCount = saved.grandfatheredFollowerCount;
        toast.success(
          gfCount > 0
            ? `${res.message || "Saved"}. ${gfCount} follower(s) are above the new max allocation (not force-adjusted).`
            : res.message || "Master configuration saved."
        );
        onSaved?.(saved);
        onClose();
      } else {
        setError(res?.message || "Failed to save master configuration.");
      }
    } catch (err) {
      setError(err?.message || "Failed to save master configuration.");
    } finally {
      setSaving(false);
    }
  };

  const traderLabel = [master?.first_name, master?.last_name].filter(Boolean).join(" ").trim()
    || master?.email_masked
    || master?.uuid
    || masterId;

  const windingDown = String(form.status || "").toUpperCase() === "WINDING_DOWN";
  const statusOptions = CONFIG_STATUS.includes(form.status)
    ? CONFIG_STATUS
    : [...CONFIG_STATUS, form.status].filter(Boolean);
  const blockedOpens =
    riskState &&
    (riskState.dailyLossBlocked || riskState.totalStopBlocked || riskState.walletReserveBlocked);

  return (
    <div className="mtc-overlay" onClick={onClose} role="presentation">
      <div
        className="mtc-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mtc-title"
      >
        <header className="mtc-modal__header">
          <div>
            <h3 id="mtc-title">
              {isEdit ? "Edit master configuration" : "Create master configuration"}
            </h3>
            <p>
              {traderLabel}
              {master?.uuid ? <span> · {master.uuid}</span> : null}
              {configVersion != null ? <span> · config v{configVersion}</span> : null}
            </p>
          </div>
          <button type="button" className="mtc-modal__close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </header>

        <form className="mtc-modal__body" onSubmit={submit}>
          {loadingConfig && (
            <div className="mtc-loading">
              <i className="fas fa-spinner fa-spin" /> Loading configuration…
            </div>
          )}
          {error && (
            <div className="mtc-error">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}
          {windingDown && (
            <div className="mtc-note">
              This master is WINDING_DOWN. New follows and opening mirrors are already stopped.
              Configuration is read-only until demotion finishes.
            </div>
          )}
          {blockedOpens && (
            <div className="mtc-note">
              New opening mirrors are blocked
              {riskState.dailyLossBlocked ? " · daily loss" : ""}
              {riskState.totalStopBlocked ? " · total stop (sticky until released)" : ""}
              {riskState.walletReserveBlocked ? " · wallet reserve" : ""}.
              Closes still copy. Release total-stop from the Risk flags tab.
            </div>
          )}

          <fieldset className="mtc-fieldset" disabled={loadingConfig || saving || windingDown}>

          <Section title="Profile" icon="fa-id-badge">
            <Field label="Display name *" hint="Required the first time a config is created.">
              <input
                type="text"
                value={form.displayName}
                onChange={(e) => setTop("displayName", e.target.value)}
                required
              />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => setTop("status", e.target.value)}>
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Description" className="mtc-field--full">
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => setTop("description", e.target.value)}
                placeholder="BTC and ETH Spot/Futures trader"
              />
            </Field>
            <Field label="Profile image URL">
              <input
                type="text"
                value={form.profileImage}
                onChange={(e) => setTop("profileImage", e.target.value)}
                placeholder="https://…"
              />
            </Field>
            <Field label="Portfolio visibility">
              <select
                value={form.portfolioVisibility}
                onChange={(e) => setTop("portfolioVisibility", e.target.value)}
              >
                {PORTFOLIO_VISIBILITY.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Maximum followers" hint="Minimum 1.">
              <input
                type="number"
                min={1}
                value={form.maximumFollowers}
                onChange={(e) => setTop("maximumFollowers", e.target.value)}
              />
            </Field>
            <Field
              label="Minimum qualifying balance (USDT)"
              hint="Required in the copy-eligible wallet while the master has at least one active follower. Internal transfers that would drop below this are blocked."
            >
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.minimumQualifyingBalance}
                onChange={(e) => setTop("minimumQualifyingBalance", e.target.value)}
              />
            </Field>
          </Section>

          <Section title="Markets & pairs" icon="fa-chart-line">
            <div className="mtc-field mtc-field--full">
              <span className="mtc-field__label">Allowed markets</span>
              <div className="mtc-chips">
                {MARKETS.map((market) => (
                  <button
                    key={market}
                    type="button"
                    className={`mtc-chip ${form.allowedMarkets.includes(market) ? "mtc-chip--on" : ""}`}
                    onClick={() => toggleMarket(market)}
                  >
                    {market}
                  </button>
                ))}
              </div>
            </div>
            {visibleSpotSymbols.length > 0 && (
              <div className="mtc-field mtc-field--full">
                <span className="mtc-field__label">Active spot pairs</span>
                <div className="mtc-chips mtc-chips--wrap">
                  {visibleSpotSymbols.map((symbol) => (
                    <button
                      key={symbol}
                      type="button"
                      className={`mtc-chip ${form.allowedPairs.includes(symbol) ? "mtc-chip--on" : ""}`}
                      onClick={() => togglePair(symbol)}
                    >
                      {symbol}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Field
              label="Additional pairs"
              hint="Uppercase symbols such as BTCUSDT. Needed for futures pairs that are not in the spot list."
              className="mtc-field--full"
            >
              <div className="mtc-pair-add">
                <input
                  type="text"
                  value={pairDraft}
                  onChange={(e) => setPairDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addDraftPairs();
                    }
                  }}
                  placeholder="BTCUSDT, ETHUSDT"
                />
                <button type="button" className="mtc-pair-add__btn" onClick={addDraftPairs}>
                  Add
                </button>
              </div>
            </Field>
            <div className="mtc-field mtc-field--full">
              <span className="mtc-field__label">Selected pairs ({form.allowedPairs.length})</span>
              {form.allowedPairs.length ? (
                <div className="mtc-chips mtc-chips--wrap">
                  {form.allowedPairs.map((symbol) => (
                    <button
                      key={symbol}
                      type="button"
                      className="mtc-chip mtc-chip--on"
                      onClick={() => togglePair(symbol)}
                      title="Remove"
                    >
                      {symbol} <i className="fas fa-times" />
                    </button>
                  ))}
                </div>
              ) : (
                <span className="mtc-field__hint">No pairs selected — empty list is allowed.</span>
              )}
            </div>
          </Section>

          <Section title="Allocation" icon="fa-wallet">
            <Field label="Type">
              <select
                value={form.allocationSettings.type}
                onChange={(e) => setNested("allocationSettings", "type", e.target.value)}
              >
                {ALLOCATION_TYPES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace("_", " ")}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Percentage (0–100)"
              hint={
                form.copyTradeSettings.mode === "FIXED_PERCENT_OF_ALLOCATION"
                  ? "Used as the fixed % of each follower’s allocation per trade."
                  : undefined
              }
            >
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={form.allocationSettings.percentage}
                onChange={(e) => setNested("allocationSettings", "percentage", e.target.value)}
                disabled={
                  form.allocationSettings.type !== "PERCENTAGE" &&
                  form.copyTradeSettings.mode !== "FIXED_PERCENT_OF_ALLOCATION"
                }
              />
            </Field>
            <Field label="Fixed amount">
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.allocationSettings.fixedAmount ?? ""}
                onChange={(e) => setNested("allocationSettings", "fixedAmount", e.target.value)}
                disabled={form.allocationSettings.type !== "FIXED_AMOUNT"}
              />
            </Field>
            <Field label="Minimum allocation">
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.allocationSettings.minimumAllocation}
                onChange={(e) =>
                  setNested("allocationSettings", "minimumAllocation", e.target.value)
                }
              />
            </Field>
            <Field label="Maximum allocation">
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.allocationSettings.maximumAllocation}
                onChange={(e) =>
                  setNested("allocationSettings", "maximumAllocation", e.target.value)
                }
              />
            </Field>
          </Section>

          <Section title="Copy trade" icon="fa-clone">
            <Field
              label="Position sizing mode"
              hint={
                COPY_MODE_HINTS[normalizeCopyMode(form.copyTradeSettings.mode)] ||
                COPY_MODE_HINTS.PROPORTIONAL
              }
              className="mtc-field--full"
            >
              <select
                value={normalizeCopyMode(form.copyTradeSettings.mode)}
                onChange={(e) => setNested("copyTradeSettings", "mode", e.target.value)}
              >
                {COPY_MODES.map((s) => (
                  <option key={s} value={s}>
                    {COPY_MODE_LABELS[s] || s.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Fixed amount per trade">
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.copyTradeSettings.fixedAmountPerTrade ?? ""}
                onChange={(e) =>
                  setNested("copyTradeSettings", "fixedAmountPerTrade", e.target.value)
                }
                disabled={form.copyTradeSettings.mode !== "FIXED_AMOUNT"}
              />
            </Field>
            <Field label="Multiplier">
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.copyTradeSettings.multiplier}
                onChange={(e) => setNested("copyTradeSettings", "multiplier", e.target.value)}
                disabled={form.copyTradeSettings.mode !== "MULTIPLIER"}
              />
            </Field>
            <Field label="Minimum amount per trade">
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.copyTradeSettings.minimumAmountPerTrade}
                onChange={(e) =>
                  setNested("copyTradeSettings", "minimumAmountPerTrade", e.target.value)
                }
              />
            </Field>
            <Field label="Maximum amount per trade">
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.copyTradeSettings.maximumAmountPerTrade}
                onChange={(e) =>
                  setNested("copyTradeSettings", "maximumAmountPerTrade", e.target.value)
                }
              />
            </Field>
            <Field label="Maximum trades per day" hint="Minimum 1.">
              <input
                type="number"
                min={1}
                value={form.copyTradeSettings.maximumTradesPerDay}
                onChange={(e) =>
                  setNested("copyTradeSettings", "maximumTradesPerDay", e.target.value)
                }
              />
            </Field>
          </Section>

          <Section title="Commission" icon="fa-percent">
            <p className="mtc-note mtc-field--full">
              Settlement is live and moves real USDT using a high-water mark: commission is only
              charged on a new high in a follower’s attributed profit from this master, never on
              recovering a prior high. Weekly job plus crystallization on unfollow / wind-down /
              manual run. See the Settlements tab for money that actually moved.
            </p>
            <Field label="Master profit share %">
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={form.commissionSettings.masterProfitSharePercentage}
                onChange={(e) =>
                  setNested("commissionSettings", "masterProfitSharePercentage", e.target.value)
                }
              />
            </Field>
            <Field label="Platform profit share %">
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={form.commissionSettings.platformProfitSharePercentage}
                onChange={(e) =>
                  setNested("commissionSettings", "platformProfitSharePercentage", e.target.value)
                }
              />
            </Field>
            <Field label="Calculate on">
              <input type="text" value="REALIZED_NET_PROFIT" readOnly />
            </Field>
            <Field label="Settlement frequency">
              <select
                value={form.commissionSettings.settlementFrequency}
                onChange={(e) =>
                  setNested("commissionSettings", "settlementFrequency", e.target.value)
                }
              >
                {SETTLEMENT_FREQUENCIES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace("_", " ")}
                  </option>
                ))}
              </select>
            </Field>
          </Section>

          <Section title="Spot settings" icon="fa-coins">
            <Field label="Spot enabled">
              <select
                value={form.spotSettings.enabled ? "true" : "false"}
                onChange={(e) => {
                  const enabled = e.target.value === "true";
                  setForm((f) => {
                    const nextMarkets = enabled
                      ? Array.from(new Set([...f.allowedMarkets, "SPOT"]))
                      : f.allowedMarkets.filter((m) => m !== "SPOT");
                    if (!enabled && !nextMarkets.length) {
                      return f;
                    }
                    return {
                      ...f,
                      spotSettings: { ...f.spotSettings, enabled },
                      allowedMarkets: nextMarkets,
                    };
                  });
                }}
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </Field>
            <div className="mtc-field">
              <span className="mtc-field__label">Allowed order types</span>
              <span className="mtc-field__hint">Disallowed types are skipped (ORDER_TYPE_NOT_ALLOWED).</span>
              <div className="mtc-chips">
                {ORDER_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`mtc-chip ${
                      form.spotSettings.allowedOrderTypes?.includes(type) ? "mtc-chip--on" : ""
                    }`}
                    onClick={() => toggleOrderType(type)}
                    disabled={!form.spotSettings.enabled}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
            <Field
              label="Maximum slippage %"
              hint="MARKET orders are capped at this slippage, not the master’s own (possibly wider) choice."
            >
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.spotSettings.maximumSlippagePercentage}
                onChange={(e) =>
                  setNested("spotSettings", "maximumSlippagePercentage", e.target.value)
                }
                disabled={!form.spotSettings.enabled}
              />
            </Field>
          </Section>

          <Section title="Futures settings" icon="fa-layer-group">
            <Field
              label="Futures enabled"
              hint="FUTURES cannot stay in allowed markets while this is off."
            >
              <select
                value={form.futuresSettings.enabled ? "true" : "false"}
                onChange={(e) => {
                  const enabled = e.target.value === "true";
                  setForm((f) => {
                    let nextMarkets = enabled
                      ? Array.from(new Set([...f.allowedMarkets, "FUTURES"]))
                      : f.allowedMarkets.filter((m) => m !== "FUTURES");
                    if (!nextMarkets.length) nextMarkets = ["SPOT"];
                    return {
                      ...f,
                      futuresSettings: { ...f.futuresSettings, enabled },
                      spotSettings: {
                        ...f.spotSettings,
                        enabled: nextMarkets.includes("SPOT"),
                      },
                      allowedMarkets: nextMarkets,
                    };
                  });
                }}
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </Field>
            <Field label="Maximum leverage" hint="Minimum 1.">
              <input
                type="number"
                min={1}
                value={form.futuresSettings.maximumLeverage}
                onChange={(e) => setNested("futuresSettings", "maximumLeverage", e.target.value)}
                disabled={!form.futuresSettings.enabled}
              />
            </Field>
            <Field label="Leverage mode">
              <select
                value={form.futuresSettings.leverageMode}
                onChange={(e) => setNested("futuresSettings", "leverageMode", e.target.value)}
                disabled={!form.futuresSettings.enabled}
              >
                {LEVERAGE_MODES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Fixed leverage">
              <input
                type="number"
                min={1}
                value={form.futuresSettings.fixedLeverage ?? ""}
                onChange={(e) => setNested("futuresSettings", "fixedLeverage", e.target.value)}
                disabled={
                  !form.futuresSettings.enabled ||
                  form.futuresSettings.leverageMode !== "FIXED"
                }
              />
            </Field>
            <Field label="Margin mode">
              <select
                value={form.futuresSettings.marginMode}
                onChange={(e) => setNested("futuresSettings", "marginMode", e.target.value)}
                disabled={!form.futuresSettings.enabled}
              >
                {MARGIN_MODES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace("_", " ")}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Maximum margin per trade">
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.futuresSettings.maximumMarginPerTrade}
                onChange={(e) =>
                  setNested("futuresSettings", "maximumMarginPerTrade", e.target.value)
                }
                disabled={!form.futuresSettings.enabled}
              />
            </Field>
            <Field
              label="Maximum open positions"
              hint="Minimum 1. Opening a new futures symbol beyond this is skipped (MAX_OPEN_POSITIONS_REACHED). Adds to an already-open symbol do not count."
            >
              <input
                type="number"
                min={1}
                value={form.futuresSettings.maximumOpenPositions}
                onChange={(e) =>
                  setNested("futuresSettings", "maximumOpenPositions", e.target.value)
                }
                disabled={!form.futuresSettings.enabled}
              />
            </Field>
          </Section>

          <Section title="Risk settings" icon="fa-shield-alt">
            <p className="mtc-note mtc-field--full">
              Enforced every 5 minutes. Daily loss and wallet reserve clear on their own. Total stop
              stays blocked until an admin releases it. All three only block new opens — closes still
              copy.
            </p>
            <Field label="Maximum daily loss %">
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={form.riskSettings.maximumDailyLossPercentage}
                onChange={(e) =>
                  setNested("riskSettings", "maximumDailyLossPercentage", e.target.value)
                }
              />
            </Field>
            <Field label="Total stop loss %">
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={form.riskSettings.totalStopLossPercentage}
                onChange={(e) =>
                  setNested("riskSettings", "totalStopLossPercentage", e.target.value)
                }
              />
            </Field>
            <Field label="Minimum wallet reserve %">
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={form.riskSettings.minimumWalletReservePercentage}
                onChange={(e) =>
                  setNested("riskSettings", "minimumWalletReservePercentage", e.target.value)
                }
              />
            </Field>
          </Section>

          {(versions.length > 0 || grandfathered.length > 0) && (
            <Section title="Version history" icon="fa-history">
              {grandfathered.length > 0 && (
                <div className="mtc-field mtc-field--full">
                  <span className="mtc-field__label">
                    Grandfathered followers ({grandfathered.length})
                  </span>
                  <p className="mtc-field__hint">
                    These ACTIVE/PAUSED followers sit above the current max allocation. A config edit
                    never force-adjusts them — it only affects trades placed after the change.
                  </p>
                  <ul className="mtc-history-list">
                    {grandfathered.map((row) => {
                      const user = row.follower || {};
                      const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
                      return (
                        <li key={row.followerId || user.uuid}>
                          {name || user.email || row.followerId || "—"}
                          {row.allocatedAmount != null
                            ? ` · allocated ${formatCopyNumber(row.allocatedAmount, 4)}`
                            : ""}
                          {row.currentMaximumAllocation != null
                            ? ` · max ${formatCopyNumber(row.currentMaximumAllocation, 4)}`
                            : ""}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {versions.length > 0 && (
                <div className="mtc-field mtc-field--full">
                  <span className="mtc-field__label">Config versions</span>
                  <ul className="mtc-history-list">
                    {versions.map((row) => (
                      <li key={row.version}>
                        v{row.version}
                        {row.effectiveFrom ? ` · ${new Date(row.effectiveFrom).toLocaleString("en-IN")}` : ""}
                        {Array.isArray(row.changedFields) && row.changedFields.length
                          ? ` · ${row.changedFields.join(", ")}`
                          : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>
          )}
          </fieldset>

          <footer className="mtc-modal__footer">
            <button type="button" className="copy-trading-modal__cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="copy-trading-promote-btn" disabled={saving || loadingConfig || windingDown}>
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Saving…
                </>
              ) : (
                <>
                  <i className="fas fa-save" /> {isEdit ? "Update configuration" : "Create configuration"}
                </>
              )}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default MasterTraderConfigModal;
